'use client';

import React, { useRef, useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const steps = [
  {
    id: 'step-join',
    number: '01',
    label: 'JOIN',
    title: 'Start With Yourself',
    description: 'Become a Richésse member and experience the products personally. Your authentic experience is your most powerful tool.',
    icon: 'UserPlusIcon',
    color: 'text-primary',
    bg: 'bg-primary/10',
  },
  {
    id: 'step-learn',
    number: '02',
    label: 'LEARN',
    title: 'Grow Your Knowledge',
    description: 'Access the Richésse Academy. Learn about products, wellness science, digital marketing, and business fundamentals.',
    icon: 'AcademicCapIcon',
    color: 'text-blue-600',
    bg: 'bg-blue-50 dark:bg-blue-950/30',
  },
  {
    id: 'step-share',
    number: '03',
    label: 'SHARE',
    title: 'Share What You Love',
    description: 'Introduce products to friends, family, and your network. Genuine recommendations create lasting customer relationships.',
    icon: 'ShareIcon',
    color: 'text-violet-600',
    bg: 'bg-violet-50 dark:bg-violet-950/30',
  },
  {
    id: 'step-sell',
    number: '04',
    label: 'SELL',
    title: 'Use Your Personal Store',
    description: 'Your replicated Richésse storefront is live from day one. Share richesse.global/yourname and start receiving orders.',
    icon: 'ShoppingBagIcon',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
  },
  {
    id: 'step-build',
    number: '05',
    label: 'BUILD',
    title: 'Grow Your Community',
    description: 'Invite others to join and support their journey. A growing team means growing collective volume and opportunity.',
    icon: 'UserGroupIcon',
    color: 'text-rose-500',
    bg: 'bg-rose-50 dark:bg-rose-950/30',
  },
  {
    id: 'step-grow',
    number: '06',
    label: 'GROW',
    title: 'Develop Your Business',
    description: 'With the right tools, education, and community behind you, your Richésse business can grow in ways you define.',
    icon: 'ChartBarIcon',
    color: 'text-amber-600',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
  },
];

export default function OpportunitySection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 lg:py-32 bg-background" ref={ref}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-16 lg:mb-20">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="section-divider" />
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">The Opportunity</span>
            <span className="section-divider" />
          </div>
          <h2 className="font-display text-section-title text-foreground mb-4">
            Your Journey with Richésse
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-base leading-relaxed">
            A clear, honest path from discovery to building a meaningful business — on your terms, at your pace.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {steps.map((step, i) => (
            <div
              key={step.id}
              className={`card-elevated p-6 lg:p-8 card-hover transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-11 h-11 rounded-xl ${step.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={step.icon as any} size={20} className={step.color} />
                </div>
                <div>
                  <span className={`text-xs font-bold tracking-widest ${step.color}`}>{step.number} — {step.label}</span>
                  <h3 className="text-base font-semibold text-foreground mt-0.5">{step.title}</h3>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>

        {/* CTA banner */}
        <div className="rounded-2xl bg-accent text-accent-foreground p-8 lg:p-12 relative overflow-hidden">
          <div className="blob-gold absolute -top-20 -right-20 w-80 h-80" />
          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="font-display text-hero-lg text-accent-foreground mb-3">
                Ready to Start Your Journey?
              </h3>
              <p className="text-accent-foreground/70 max-w-lg text-base leading-relaxed">
                Join thousands of members who are building their Richésse story. Your personal store is ready the moment you sign up.
              </p>
              <p className="text-xs text-accent-foreground/40 mt-3">
                * Results vary. Income is not guaranteed. Please review our Income Disclosure Statement before joining.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link href="/sign-up-login-screen" className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold whitespace-nowrap">
                Join Richésse Today
                <Icon name="ArrowRightIcon" size={16} />
              </Link>
              <Link href="/product-catalog" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold bg-white/10 text-accent-foreground border border-white/20 hover:bg-white/20 transition-colors whitespace-nowrap">
                Shop Products First
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}